import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import HoverTooltip from "../HoverTooltip.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import {
  ASKABLE_VERDICTS, NEW_STANDARD_START, STANDARD_MAX_CHARS, STANDARD_NAME_MAX,
  answerFor, standardLabel, standardTally, unaskedUnder, withAnyStandard,
} from "../../lib/triageLite.js";
import {
  createRun, fetchAnswers, getRun, listRuns, listStandards, previewRun, retireStandard, setStandardLive,
  standardIdFor, triageErrorText, verificationWindow, writeStandard, MAX_RUN_FINDINGS,
  MAX_TENANT_WIDE_RUN_DAYS,
} from "../../lib/findingsTriage.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { ACTION_CHIP, CAPS_CHIP, FILE_LABEL, RUNG_CHIP } from "./triageParts.jsx";
import { REST_MAX, StandardTryResults, useStandardTry } from "./StandardTry.jsx";
import { PREVIEW_MAX_FINDINGS } from "../../lib/standardPreview.js";
import { SEVERITY_COLOR } from "../../lib/traceReview.js";

// The controls the lens already draws: the primary button's weight for what
// spends, the action chip for the rest, the Search field's box for text.
const PRIMARY = "cursor-pointer rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)] disabled:cursor-default disabled:opacity-50";
const FIELD = "w-full rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] py-[6px] text-[12px] leading-[1.35] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--line-strong)] disabled:opacity-60";
// A box the standard's work opens in, under its line: the lens's status box.
const BOX = "grid gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px]";
const TEXT = "text-[11px] leading-[1.35] text-[color:var(--muted)]";
const LINK = "cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-[1.35] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]";
const omit = (o, key) => { const next = { ...o }; delete next[key]; return next; };
const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
const usd = (n) => (n >= 0.01 ? `$${n.toFixed(2)}` : n > 0 ? "<$0.01" : "$0");
const when = (iso) => {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "";
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const d = new Date(ms);
  return d.toDateString() === new Date().toDateString() ? `today ${d.toTimeString().slice(0, 5)}` : d.toISOString().slice(0, 10);
};
// How often a re-apply run is checked while it asks.
export const RUN_POLL_MS = 5000;
const clientError = (e) => Number(e?.status) >= 400 && Number(e?.status) < 500;

// A view setting of this browser's: which standard the bar shows.
const IN_USE_KEY = "triage.lite.standardInUse";
const readKey = (key) => { try { return localStorage.getItem(key) || ""; } catch { return ""; } };
const writeKey = (key, value) => { try { if (value) localStorage.setItem(key, value); else localStorage.removeItem(key); } catch { /* per view only */ } };

// The tenant's escalation standards and every answer each gave the findings in
// view. Standards are asked by the backend: of each new verification while a
// standard is live, and of the verifications in a scope and window when someone
// re-applies one. Switching the standard in use re-sorts the list without
// asking anything. `scope` is the sessions a re-apply covers, null for the
// whole organization.
export function useLiteStandards({ scopeKey, lite, canWrite = false, enabled = true, scope = null }) {
  const verdicts = lite?.verdicts;
  const [items, setItems] = useState([]);
  const [listed, setListed] = useState(false);
  const [inUseId, setInUseId] = useState(() => readKey(IN_USE_KEY));
  const [answers, setAnswers] = useState({});
  // The findings and standards the answers in hand were read for, so a view
  // that counts by them can wait for the ones it is showing.
  const [answeredFor, setAnsweredFor] = useState(null);
  const [answersError, setAnswersError] = useState(null);
  const [job, setJob] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [listNonce, setListNonce] = useState(0);
  const [answerNonce, setAnswerNonce] = useState(0);
  // Which run this view follows; a poll for any other is dropped.
  const following = useRef(null);
  const pollRef = useRef(null);
  const stopFollowing = useCallback(() => { following.current = null; clearTimeout(pollRef.current); }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    listStandards().then((list) => { if (live) { setItems(list); setListed(true); } }, (e) => { if (live) { setError(triageErrorText(e)); setListed(true); } });
    return () => { live = false; };
  }, [scopeKey, listNonce, enabled]);
  const ids = useMemo(() => Object.keys(verdicts || {}), [verdicts]);
  const digests = useMemo(() => items.map((s) => s.digest).filter(Boolean), [items]);
  const answersKey = `${ids.join(",")}|${digests.join(",")}`;
  useEffect(() => {
    if (!ids.length || !digests.length || !lite?.readWindow) { setAnswers({}); setAnswersError(null); setAnsweredFor(answersKey); return undefined; }
    const controller = new AbortController();
    fetchAnswers({ ids, reviewWindow: lite.readWindow, verdicts, digests, sensitive: lite.sensitive, signal: controller.signal, fresh: answerNonce > 0 })
      .then(
        (next) => { if (!controller.signal.aborted) { setAnswers(next); setAnswersError(null); setAnsweredFor(answersKey); } },
        (e) => { if (!controller.signal.aborted) { setAnswers({}); setAnswersError(e); setAnsweredFor(answersKey); } },
      );
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, digests, verdicts, lite?.readWindow, lite?.sensitive, answerNonce]);
  // The answers are the current findings' and standards', once the standards are listed.
  const answersReady = listed && answeredFor === answersKey;
  // A new scope is a new question: whatever this view was following or about
  // to start belongs to the last one.
  useEffect(() => () => { stopFollowing(); setJob(null); setPlan(null); }, [scopeKey, stopFollowing]);
  const inUse = items.find((s) => s.id === inUseId) || items.find((s) => s.live) || items[0] || null;
  const lib = useMemo(() => ({ items, inUse: inUse?.id || null }), [items, inUse?.id]);
  const adopt = useCallback((id) => { writeKey(IN_USE_KEY, id); setInUseId(id); }, []);
  const askable = useMemo(() => Object.entries(verdicts || {}).filter(([, v]) => ASKABLE_VERDICTS.includes(v?.verdict)).map(([id]) => id), [verdicts]);
  const unasked = useCallback((item, only = null) => (item ? unaskedUnder(verdicts, answers, item.digest, only) : []), [verdicts, answers]);

  const guard = useCallback(async (fn) => {
    setError("");
    try { return await fn(); } catch (e) { setError(triageErrorText(e)); if (e?.grpcCode === "ABORTED") setListNonce((n) => n + 1); return null; }
  }, []);
  // A run is the backend's: this only starts it and follows its counts until
  // it finishes, then reads the answers it published. A refusal (the run is
  // gone, or this role may not read it) ends the following; anything else is
  // retried on the next tick. `expected` is what the count before the click
  // found: a run reports its selection only once the worker first takes it.
  const follow = useCallback((run, label, expected = 0) => {
    stopFollowing();
    following.current = run.runId;
    const show = (r) => setJob({ runId: r.runId, label, running: !r.done, done: r.asked, total: r.selected || expected, met: r.met, costUsd: r.costUsd, error: r.status === "failed" ? "the run failed" : r.status === "retired" ? "the standard was removed" : "" });
    show(run);
    if (run.done) { setAnswerNonce((n) => n + 1); return; }
    const tick = () => getRun(run.runId).then((r) => {
      if (following.current !== run.runId) return;
      show(r);
      if (r.done) setAnswerNonce((n) => n + 1);
      else pollRef.current = setTimeout(tick, RUN_POLL_MS);
    }, (e) => {
      if (following.current !== run.runId) return;
      if (clientError(e)) { setJob((cur) => (cur ? { ...cur, running: false, error: triageErrorText(e) } : cur)); following.current = null; return; }
      pollRef.current = setTimeout(tick, RUN_POLL_MS);
    });
    pollRef.current = setTimeout(tick, RUN_POLL_MS);
  }, [stopFollowing]);
  // A run this tenant left asking is followed again on return, so it is not
  // started twice for want of seeing it.
  useEffect(() => {
    if (!enabled || !items.length) return undefined;
    let live = true;
    listRuns().then((runs) => {
      if (!live || following.current) return;
      const active = runs.find((r) => !r.done && items.some((s) => s.digest === r.digest));
      const item = active && items.find((s) => s.digest === active.digest);
      if (active && item) follow(active, standardLabel(item));
    }, () => { /* the bar works without it */ });
    return () => { live = false; };
  }, [enabled, items, follow]);
  useEffect(() => () => stopFollowing(), [stopFollowing]);

  // A re-apply is counted before it is started: the preview runs the selection
  // the run will, so the number on the button is what the run does.
  const runArgs = useCallback((item, window) => {
    const span = verificationWindow(window);
    return { standardId: item.standardId, standardVersion: item.version, fromMs: span.fromMs, toMs: span.toMs, scope };
  }, [scope]);
  const preview = useCallback((item, window) => guard(async () => {
    if (!item || !window) return;
    // The run is started with these same arguments, so it asks what was counted.
    const args = runArgs(item, window);
    setPlan({ item, args, loading: true });
    const counts = await previewRun(args);
    setPlan((cur) => (cur && cur.item.id === item.id ? { item, args, loading: false, ...counts } : cur));
  }).then((done) => { if (done === null) setPlan(null); }), [guard, runArgs]);
  const cancelPlan = useCallback(() => setPlan(null), []);
  const ask = useCallback(() => guard(async () => {
    if (!plan || plan.loading) return;
    const run = await createRun(plan.args);
    setPlan(null);
    follow(run, standardLabel(plan.item), plan.inScope);
  }), [guard, plan, follow]);
  // A check of older findings started from elsewhere (a suggestion saved in
  // the chat), counted and limited as the bar's own check is, and followed here.
  // Resolves to { run } or { error }, never throws.
  const backfill = useCallback(async (item, window) => {
    if (!item || !window) return { error: "No time range to check." };
    if (!scope?.length && window.toMs - window.fromMs > MAX_TENANT_WIDE_RUN_DAYS * 86400000) {
      return { error: `Across the organization you can check up to ${MAX_TENANT_WIDE_RUN_DAYS} days at once. Check older findings from the Triage tab with a shorter time range.` };
    }
    try {
      const args = runArgs(item, window);
      const counts = await previewRun(args);
      if (counts.inScope > MAX_RUN_FINDINGS) {
        return { error: `That's ${counts.inScope.toLocaleString()} findings, more than one check covers (${MAX_RUN_FINDINGS.toLocaleString()}). Check older findings from the Triage tab with a shorter time range.` };
      }
      const run = await createRun(args);
      follow(run, standardLabel(item), counts.inScope);
      return { run };
    } catch (e) {
      return { error: triageErrorText(e) };
    }
  }, [scope, runArgs, follow]);
  const create = useCallback((name, text, live) => guard(async () => {
    const item = await writeStandard({ standardId: standardIdFor(name), expectedVersion: 0, name: name.trim(), text, live });
    setItems((cur) => [...cur, item]);
    adopt(item.id);
    return item;
  }), [guard, adopt]);
  // A revision leaves the live setting to the server: another admin may have
  // paused it since this list was read.
  const revise = useCallback((text) => guard(async () => {
    if (!inUse) return null;
    const item = await writeStandard({ standardId: inUse.standardId, expectedVersion: inUse.version, name: inUse.name, text });
    setItems((cur) => cur.map((s) => (s.standardId === item.standardId ? item : s)));
    adopt(item.id);
    return item;
  }), [guard, inUse, adopt]);
  // A new version of a given standard (the tuning chat's), on the version it
  // was read at, so a revision made meanwhile is refused rather than overwritten.
  const reviseItem = useCallback((item, text) => guard(async () => {
    const next = await writeStandard({ standardId: item.standardId, expectedVersion: item.version, name: item.name, text });
    setItems((cur) => cur.map((s) => (s.standardId === next.standardId ? next : s)));
    return next;
  }), [guard]);
  const setLive = useCallback((item, live) => guard(async () => {
    const next = await setStandardLive({ standardId: item.standardId, expectedVersion: item.version, live });
    setItems((cur) => cur.map((s) => (s.standardId === next.standardId ? next : s)));
  }), [guard]);
  const retire = useCallback((item) => guard(async () => {
    await retireStandard({ standardId: item.standardId, expectedVersion: item.version });
    setItems((cur) => cur.filter((s) => s.standardId !== item.standardId));
    if (plan?.item?.id === item.id) setPlan(null);
    return true;
  }), [guard, plan]);
  const dismiss = useCallback(() => { stopFollowing(); setJob(null); setError(""); }, [stopFollowing]);
  // With one standard kept there is nothing to add up, so "any" is the one.
  // The list shows what any kept standard flagged; the one in use is the one
  // the bar edits, tries and checks older findings with.
  const anyOn = items.length > 1;
  // Only the standards that are on count: one turned off stops flagging what
  // it flagged, and its answers are kept for when it is turned back on.
  const liveItems = useMemo(() => items.filter((s) => s.live), [items]);
  const effective = useMemo(
    () => (items.length ? withAnyStandard(verdicts, answers, liveItems, inUse?.id) : (verdicts || {})),
    [items.length, verdicts, answers, liveItems, inUse],
  );
  // The list read again: a standard written elsewhere (Investigate's
  // suggested edits) shows here once it is.
  const reload = useCallback(() => setListNonce((n) => n + 1), []);
  return {
    lib, inUse, answers, answersError, answersReady, verdicts, effective, askable, job, plan, error, unasked,
    preview, cancelPlan, ask, backfill, create, revise, reviseItem, retire, adopt, setLive, dismiss, canWrite, scope, any: anyOn, reload,
    // Trying a wording reads the verifier's text and the finding's own, which
    // only a role that may read session content can.
    sensitive: lite?.sensitive !== false,
  };
}

const Track = ({ on }) => (
  <span aria-hidden="true" className={`relative inline-block h-[10px] w-[18px] shrink-0 rounded-full border transition-colors ${on ? "border-[color:var(--ink)] bg-[color:var(--ink)]" : "border-[color:var(--line-strong)]"}`}>
    <span className={`absolute top-[1px] h-[6px] w-[6px] rounded-full transition-all ${on ? "left-[9px] bg-[color:var(--bg)]" : "left-[1px] bg-[color:var(--muted)]"}`} />
  </span>
);

// What a standard's switch does, said before the click: pausing costs the
// findings verified while it is paused, which are never asked unless someone
// re-applies the standard.
export function liveHint(item, { canWrite = true, running = false } = {}) {
  if (!canWrite) return "You need permission to change tenant settings to turn standards on or off.";
  if (running) return "A check is running. You can turn standards on or off once it finishes.";
  return item.live
    ? "On: every new finding is checked against this standard. Turn it off and new findings won't be checked until you turn it back on."
    : "Off: new findings aren't checked against this standard. Turn it on to check new ones. You can check the ones it missed from here.";
}

// A standard's switch, on its own row: it acts on that standard without
// opening it.
function LiveSwitch({ item, canWrite, running, onFlip }) {
  return (
    <HoverTooltip label={liveHint(item, { canWrite, running })} placement="left" wide>
      <button
        type="button"
        role="switch"
        data-lite-std-live={item.id}
        aria-checked={item.live}
        aria-label={`${item.name}: ${item.live ? "on" : "off"}`}
        disabled={!canWrite || running}
        onClick={() => onFlip(item)}
        className="inline-flex w-[44px] shrink-0 cursor-pointer items-center gap-[5px] border-0 bg-transparent p-0 text-[11px] leading-[15px] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-50"
      >
        <Track on={item.live} />
        {item.live ? "On" : "Off"}
      </button>
    </HoverTooltip>
  );
}

const TAB = "flex-1 cursor-pointer border-0 border-b bg-transparent px-[8px] py-[7px] text-[10px] leading-[1.2] tracking-[0.08em] uppercase";

// The tenant's standards down the side of the workbench, the ones checking new
// findings and the ones turned off on two tabs, the Off tab marked while any is
// off so a pause is seen. Each row has what it flagged and its switch; picking
// a row opens it beside the list. Turning one off asks first, since what is
// verified meanwhile is never checked. A new standard is offered at the foot.
function StandardsList({ std, items, tab, onTab, creating, canWrite, running, canNew, onPick, onNew }) {
  const { inUse, verdicts, answers } = std;
  const [confirming, setConfirming] = useState(null);
  const flip = (item) => {
    if (item.live) { setConfirming(confirming === item.id ? null : item.id); return; }
    setConfirming(null);
    std.setLive(item, true);
  };
  const on = items.filter((s) => s.live);
  const off = items.filter((s) => !s.live);
  const shown = tab === "off" ? off : on;
  const tabClass = (id) => `${TAB} ${tab === id ? "border-[color:var(--ink)] font-medium text-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`;
  return (
    <div data-lite-std-list="" className="grid content-start">
      <div role="tablist" aria-label="Escalation standards" className="flex">
        <button type="button" role="tab" data-lite-std-tab="on" aria-selected={tab === "on"} onClick={() => onTab("on")} className={tabClass("on")}>On {on.length}</button>
        <button type="button" role="tab" data-lite-std-tab="off" aria-selected={tab === "off"} onClick={() => onTab("off")} className={tabClass("off")}>
          {off.length ? <span data-lite-std-off-dot="" aria-hidden="true" className="mr-[5px] inline-block h-[6px] w-[6px] rounded-full align-[1px]" style={{ background: SEVERITY_COLOR.warn }} /> : null}
          Off {off.length}
        </button>
      </div>
      <div role="tabpanel" className="grid content-start">
        {tab === "off" && off.length ? <span className={`${TEXT} px-[10px] pt-[7px] pb-[2px]`}>New findings skip these.</span> : null}
        {!shown.length ? <span data-lite-std-tab-empty="" className={`${TEXT} px-[10px] py-[9px]`}>{tab === "off" ? "None off. Every standard checks new findings." : "None on, so new findings aren’t checked."}</span> : null}
        {shown.map((s, i) => {
          const t = standardTally(verdicts, answers, s.digest);
          const picked = !creating && s.id === inUse?.id;
          return (
            <div
              key={s.id}
              data-lite-std-item={s.id}
              data-picked={picked ? "" : undefined}
              className={`grid gap-[6px] px-[10px] py-[7px] ${i ? "border-t border-[color:var(--line-soft)]" : ""} ${picked ? "bg-[color:var(--bg)] shadow-[inset_2px_0_0_var(--ink)]" : ""}`}
            >
              <div className="flex min-w-0 items-center gap-x-[10px]">
                <button
                  type="button"
                  data-lite-std-pick={s.id}
                  aria-current={picked || undefined}
                  onClick={() => { setConfirming(null); onPick(s); }}
                  className="flex min-w-0 flex-1 cursor-pointer items-baseline gap-x-[6px] border-0 bg-transparent p-0 text-left text-[12px] leading-[1.2] text-[color:var(--ink)]"
                >
                  <span className="min-w-0 truncate">{s.name}</span>
                  <span className="font-mono text-[10px] text-[color:var(--muted-soft)]">v{s.version}</span>
                  <span className="ml-auto whitespace-nowrap pl-[8px] text-[11px] text-[color:var(--muted)]">{t.met.toLocaleString()} flagged</span>
                </button>
                <LiveSwitch item={s} canWrite={canWrite} running={running} onFlip={flip} />
              </div>
              {confirming === s.id ? (
                <div data-lite-std-pause-confirm={s.id} className="grid gap-[6px] rounded-[2px] border border-[color:var(--line-strong)] px-[8px] py-[6px]">
                  <span className="text-[11px] leading-[1.35] text-[color:var(--ink)]">
                    Turn off {s.name}? New findings won&rsquo;t be checked against it until you turn it back on.
                  </span>
                  <span className="flex items-center gap-[6px]">
                    <button type="button" data-lite-std-pause-go="" onClick={() => { setConfirming(null); std.setLive(s, false); }} className={PRIMARY}>Turn off</button>
                    <button type="button" onClick={() => setConfirming(null)} className={ACTION_CHIP}>Cancel</button>
                  </span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        data-lite-std-new=""
        aria-expanded={creating}
        onClick={onNew}
        disabled={!canNew}
        className={`m-[8px] grid cursor-pointer gap-[2px] rounded-[2px] border border-dashed px-[10px] py-[8px] text-left transition-colors hover:border-[color:var(--ink)] disabled:cursor-default disabled:opacity-40 ${creating ? "border-[color:var(--ink)] bg-[color:var(--bg)]" : "border-[color:var(--line-strong)] bg-transparent"}`}
      >
        <span className="text-[12px] leading-[1.25] text-[color:var(--ink)]"><span aria-hidden="true" className="mr-[5px]">+</span>Write a new standard</span>
        <span className="text-[11px] leading-[1.3] text-[color:var(--muted)]">Try it on past findings before saving</span>
      </button>
    </div>
  );
}

// A wording's try: every true or partly true finding in the tab's span, up to
// what one try asks, or a cheaper sample of the newest. The estimate is what
// the answers so far cost, once there are some.
function TryControl({ trial, canTry, tryBlocked, fromWindow, onTry }) {
  if (trial.running) return <button type="button" data-lite-std-try-stop="" onClick={trial.stop} className={ACTION_CHIP}>Stop trying</button>;
  const all = Math.min(trial.of, REST_MAX);
  const label = !canTry ? "Try"
    : trial.of === 1 ? `Try on 1 finding${fromWindow}`
      : trial.of <= REST_MAX ? `Try on all ${plural(trial.of, "finding")}${fromWindow}`
        : `Try on the newest ${all.toLocaleString()} of ${trial.of.toLocaleString()}${fromWindow}`;
  return (
    <>
      <button type="button" data-lite-std-try-go="" disabled={!canTry} title={tryBlocked || undefined} onClick={() => onTry(all)} className={`${ACTION_CHIP} disabled:cursor-default disabled:opacity-40`}>{label}</button>
      {canTry && trial.of > PREVIEW_MAX_FINDINGS ? (
        <span className={TEXT}>
          {trial.perAnswer ? `~${usd(trial.perAnswer * all)} · ` : ""}or{" "}
          <button type="button" data-lite-std-try-sample="" onClick={() => onTry(PREVIEW_MAX_FINDINGS)} className={LINK}>just the {PREVIEW_MAX_FINDINGS} newest</button>
        </span>
      ) : null}
    </>
  );
}

// What a standard is, on the bar, for a reader who has not met one.
const STANDARDS_HINT = (
  <span className="grid gap-[6px]">
    <span>
      A standard describes in plain English <strong className="font-medium">which findings you should look into</strong>.
      You can <strong className="font-medium">test</strong> new standards here in the dashboard, and if you like the results, <strong className="font-medium">save</strong> them.
    </span>
    <span>
      Each triaged finding is checked against every standard that&rsquo;s <strong className="font-medium">on</strong>. If one fits, the finding is flagged here.
      Standards that are <strong className="font-medium">off</strong> skip new findings.
    </span>
  </span>
);

// The standards as a line above the list: how many are on, and how far the one
// open has checked the window. "Edit standards" opens the workbench in place:
// the standards down the side, and the one picked beside them, with its
// wording to change and try, what it has not checked yet, and its removal. A
// new standard opens in the same place. `runWindow` is the review window a
// check of older findings covers; `scopeLabel` names the sessions it covers,
// and `scopeCapped` says the scope held more than a check can.
export function LiteStandardBar({ std, windowIds = [], windowName = "this window", runWindow = null, scopeLabel = "this organization", scopeCapped = false, onSuggest = null, onTune = null }) {
  // Said after a count: " from the last 24 hours", or nothing for all time.
  const fromWindow = windowName === "all time" ? "" : ` from ${windowName}`;
  const { lib, inUse, verdicts, answers, job, plan, canWrite } = std;
  // Refused by the API too; said here before the click rather than after it.
  const overLimit = Boolean(plan && !plan.loading && plan.inScope > MAX_RUN_FINDINGS);
  // The API refuses an unscoped run over a week, since counting it reads the
  // whole window; scoped to sessions it may cover the lens's full range.
  const tooLongUnscoped = Boolean(runWindow && !std.scope?.length && runWindow.toMs - runWindow.fromMs > MAX_TENANT_WIDE_RUN_DAYS * 86400000);
  const [open, setOpen] = useState(false);
  // The list's tab: the one holding the standard in view when it opens.
  const [tab, setTab] = useState("on");
  // Turning the open standard on or off takes the list to its new tab, so it
  // stays in sight.
  const openLive = inUse ? inUse.live : null;
  useEffect(() => { if (openLive !== null) setTab(openLive ? "on" : "off"); }, [inUse?.id, openLive]);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [name, setName] = useState("");
  const [newText, setNewText] = useState("");
  const [live, setLive] = useState(true);
  // Unsaved wordings, per standard, so picking another one loses nothing.
  const [drafts, setDrafts] = useState({});
  const windowAskable = windowIds.filter((id) => ASKABLE_VERDICTS.includes(verdicts?.[id]?.verdict));
  const owedHere = std.unasked(inUse, windowAskable).length;
  const running = Boolean(job?.running);
  const lastAt = Object.values(answers?.[inUse?.digest] || {}).map((a) => a?.at).filter(Boolean).sort().at(-1);
  const editText = inUse ? (drafts[inUse.id] ?? inUse.text ?? "") : "";
  const setEditText = (v) => { if (inUse) setDrafts((d) => ({ ...d, [inUse.id]: v })); };
  const draftDiffers = Boolean(inUse) && editText.trim() !== (inUse.text || "").trim();
  // A wording can be tried before it is saved: asked of findings already
  // verified in the window, from this browser, with nothing stored. Trying
  // reads session content and writes nothing, so a reader who may not save a
  // standard may still try one. A new version of a saved standard is compared
  // with that standard as it stands; a new standard has nothing to compare with.
  const was = useCallback((id) => {
    const a = inUse ? answerFor(answers, inUse.digest, id, verdicts?.[id]) : null;
    return !a ? null : a.escalates ? "met" : a.unconfirmed ? "unconfirmed" : "not_met";
  }, [inUse, answers, verdicts]);
  const trial = useStandardTry({ ids: windowAskable, runWindow, was: creating ? null : was });
  const canTry = Boolean(std.sensitive && runWindow && windowAskable.length);
  const tryBlocked = !std.sensitive
    ? "Trying a wording reads the verified findings' text, which needs permission to read sensitive AI session data."
    : !runWindow || !windowAskable.length ? `No true or partly true findings${fromWindow} to try it on.` : "";
  // A fixed order, newest first: picking one highlights it where it is.
  const items = [...(lib?.items || [])].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)) || String(a.name).localeCompare(String(b.name)));
  const liveCount = items.filter((s) => s.live).length;
  const offCount = items.length - liveCount;
  const locked = !canWrite || running;
  const canNew = !running && (canWrite || canTry);

  const leave = () => { trial.reset(); setRemoving(false); setCreating(false); };
  const startNew = () => { leave(); setCreating(true); setOpen(true); setName(""); setNewText(NEW_STANDARD_START); setLive(true); };
  const pick = (item) => { leave(); if (plan && plan.item.id !== item.id) std.cancelPlan(); if (item.id !== inUse?.id) std.adopt(item.id); };
  const toggle = () => {
    if (open) { leave(); std.cancelPlan(); } else setTab(inUse && !inUse.live ? "off" : "on");
    setOpen(!open);
  };
  const saveNew = async () => { const item = await std.create(name, newText, live); if (item) leave(); };
  const saveEdit = async () => {
    const oldId = inUse.id;
    const item = await std.revise(editText);
    if (item) { trial.reset(); setDrafts((d) => omit(d, oldId)); }
  };
  const undo = () => { trial.reset(); setDrafts((d) => omit(d, inUse.id)); };
  const checkHint = tooLongUnscoped
    ? `Across ${scopeLabel} you can check up to ${MAX_TENANT_WIDE_RUN_DAYS} days at once. Pick a shorter time range, or narrow to specific sessions.`
    : inUse ? `Check every finding${fromWindow} against ${standardLabel(inUse)}. Findings it already checked are skipped.` : "";

  return (
    <div data-lite-std="" className="grid gap-[8px]">
      <div data-lite-std-box="" className="overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)]">
      <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px] px-[10px] py-[7px]">
        <HoverTooltip label={STANDARDS_HINT} placement="top" wide>
          <span data-lite-std-about="" className="inline-flex cursor-help flex-wrap items-baseline gap-x-[10px]">
            <span className={FILE_LABEL}>Standards</span>
            {inUse ? (
              <span data-lite-std-state="" className="text-[12px] leading-[1.3] text-[color:var(--ink)]">
                {liveCount
                  ? `Checking with ${plural(liveCount, "standard")}`
                  : <span data-lite-std-none-live="" style={{ color: SEVERITY_COLOR.warn }}>None on, so new findings aren&rsquo;t checked</span>}
                {liveCount && offCount ? <span className="text-[color:var(--muted)]">{" \u00b7 "}<span data-lite-std-off-count="" style={{ color: SEVERITY_COLOR.warn }}>{offCount} off</span></span> : null}
              </span>
            ) : null}
          </span>
        </HoverTooltip>
        {inUse ? (
          <>
            {onSuggest ? <button type="button" data-lite-std-suggest="" onClick={onSuggest} className={`${ACTION_CHIP} ml-auto`}>Suggest standards</button> : null}
            <button type="button" data-lite-std-menu="" aria-expanded={open} onClick={toggle} className={`${ACTION_CHIP} ${onSuggest ? "" : "ml-auto"}`}>
              {open ? "Done" : "Edit standards"}
            </button>
          </>
        ) : (
          <>
            <span data-lite-std-none="" className={TEXT}>No standards yet. Five defaults are added after the first finding is checked{canWrite ? ", or you can create your own" : ""}.</span>
            {onSuggest && !creating ? <button type="button" data-lite-std-suggest="" onClick={onSuggest} className={`${ACTION_CHIP} ml-auto`}>Suggest standards</button> : null}
            {creating ? null : <button type="button" data-lite-std-new="" onClick={startNew} disabled={!canNew} className={`${ACTION_CHIP} ${onSuggest ? "" : "ml-auto"} disabled:cursor-default disabled:opacity-40`}>New standard</button>}
          </>
        )}
      </div>

      {(open && inUse) || creating ? (
        <div data-lite-std-panel="" className={`grid border-t border-[color:var(--line)] ${inUse ? "md:grid-cols-[300px_minmax(0,1fr)]" : ""}`}>
          {inUse ? (
            <div className="border-b border-[color:var(--line)] md:border-r md:border-b-0">
              <StandardsList std={std} items={items} tab={tab} onTab={setTab} creating={creating} canWrite={canWrite} running={running} canNew={canNew} onPick={pick} onNew={startNew} />
            </div>
          ) : null}

          {creating ? (
            <div data-lite-std-form="new" className="grid content-start gap-[8px] px-[12px] py-[10px]">
              <span className={FILE_LABEL}>New standard</span>
              <input data-lite-std-new-name="" value={name} onChange={(e) => setName(e.target.value)} maxLength={STANDARD_NAME_MAX} placeholder="Name, e.g. Data leaving the machine" aria-label="Name of the new standard" className={FIELD} />
              <textarea
                data-lite-std-new-text=""
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                // Untouched, the opening is there to be finished: the cursor goes after it.
                onFocus={(e) => { if (e.target.value === NEW_STANDARD_START) { const end = e.target.value.length; e.target.setSelectionRange(end, end); } }}
                rows={6} maxLength={STANDARD_MAX_CHARS} spellCheck={false} aria-label="Wording of the new standard" className={`resize-y ${FIELD}`} />
              <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
                <label className={`${TEXT} inline-flex items-center gap-[6px]`}>
                  <input type="checkbox" data-lite-std-new-live="" checked={live} onChange={(e) => setLive(e.target.checked)} />
                  Check new findings
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[6px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[8px] py-[6px]">
                <TryControl trial={trial} canTry={canTry && Boolean(newText.trim())} tryBlocked={tryBlocked} fromWindow={fromWindow} onTry={(count) => trial.run(newText, { count })} />
                <span className="ml-auto flex items-center gap-[6px]">
                  <button type="button" data-lite-std-new-cancel="" onClick={() => { leave(); if (!inUse) setOpen(false); }} className={ACTION_CHIP}>Cancel</button>
                  <button type="button" data-lite-std-save="" disabled={!canWrite || !name.trim() || !newText.trim()} onClick={saveNew} className={PRIMARY}>Save</button>
                </span>
              </div>
              <span data-lite-std-try-note="" className={NOTE_CLASS}>
                {canTry ? "Try runs in your browser, the same way saved standards are checked. Nothing is saved until you press Save." : tryBlocked}
                {canWrite ? null : " You need permission to change tenant settings to save it."}
              </span>
              <StandardTryResults trial={trial} text={newText} windowName={windowName} />
              <span className={NOTE_CLASS}>Once saved, it checks new findings. You can then check earlier ones from here.</span>
            </div>
          ) : (
            <div data-lite-std-form="edit" className="grid content-start gap-[8px] px-[12px] py-[10px]">
              <div className="flex flex-wrap items-baseline gap-x-[8px] gap-y-[4px]">
                <span data-lite-std-open-name="" className="text-[13px] leading-[1.2] text-[color:var(--ink)]">{inUse.name}</span>
                <span className="font-mono text-[10px] text-[color:var(--muted-soft)]">v{inUse.version}</span>
                <span data-lite-std-status="" className={TEXT}>
                  {inUse.live ? `Checks every new finding${lastAt ? ` · last ran ${when(lastAt)}` : ""}` : "Off, so new findings aren’t checked against it"}
                </span>
                {onTune ? <button type="button" data-lite-std-tune="" onClick={() => onTune(inUse)} className={`${ACTION_CHIP} ml-auto`}>Tune with chat</button> : null}
                <button type="button" data-lite-std-remove="" aria-expanded={removing} onClick={() => setRemoving((v) => !v)} disabled={locked} className={`${ACTION_CHIP} ${onTune ? "" : "ml-auto"} disabled:cursor-default disabled:opacity-40`}>Remove</button>
              </div>

              {removing ? (
                <div data-lite-std-remove-confirm="" className="grid gap-[6px] rounded-[2px] border border-[color:var(--line-strong)] px-[8px] py-[6px]">
                  <span className="text-[11px] leading-[1.35] text-[color:var(--ink)]">Remove {inUse.name}? It stops checking new findings and leaves this list. What it already flagged is kept.</span>
                  <span className="flex items-center gap-[6px]">
                    <button type="button" data-lite-std-remove-go="" onClick={async () => { if (await std.retire(inUse)) setRemoving(false); }} className={PRIMARY}>Remove</button>
                    <button type="button" onClick={() => setRemoving(false)} className={ACTION_CHIP}>Cancel</button>
                  </span>
                </div>
              ) : null}

              {runWindow && owedHere ? (
                <div data-lite-std-todo="" className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px] rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[6px]">
                  <span className="min-w-[180px] flex-1 text-[11px] leading-[1.35] text-[color:var(--ink)]">
                    {plural(owedHere, "finding")}{fromWindow} {owedHere === 1 ? "was" : "were"} never checked against this.
                  </span>
                  <HoverTooltip label={checkHint} placement="top" wide>
                    <button type="button" data-lite-std-window="" data-too-long={tooLongUnscoped ? "" : undefined} aria-expanded={Boolean(plan)} onClick={() => (plan ? std.cancelPlan() : std.preview(inUse, runWindow))} disabled={locked || tooLongUnscoped} className={`${ACTION_CHIP} disabled:cursor-default disabled:opacity-40`}>
                      {owedHere === 1 ? "Check it" : `Check those ${owedHere.toLocaleString()}`}
                    </button>
                  </HoverTooltip>
                </div>
              ) : null}

              {plan ? (
                <div data-lite-std-plan="" className="grid gap-[8px] rounded-[2px] border border-[color:var(--line-strong)] px-[8px] py-[6px]">
                  {plan.loading ? (
                    <span className={TEXT}>Counting findings to check{"…"}</span>
                  ) : (
                    <>
                      <span data-lite-std-plan-count="" className="text-[11px] leading-[1.35] text-[color:var(--ink)]">
                        {plan.inScope
                          ? <>Check <strong className="font-medium">{plural(plan.inScope, "finding")}</strong>{fromWindow} in {scopeLabel} against {standardLabel(plan.item)}{" · "}{plan.notYetAsked.toLocaleString()} not checked yet</>
                          : <>No findings{fromWindow} in {scopeLabel} to check.</>}
                      </span>
                      {plan.inScope && !plan.notYetAsked ? <span className={TEXT}>All of them are already checked against this version, so there&rsquo;s nothing to do.</span> : null}
                      {overLimit ? <span data-lite-std-plan-over="" className={NOTE_CLASS}>You can check up to {MAX_RUN_FINDINGS.toLocaleString()} findings at once. Pick a shorter time range or fewer sessions.</span> : null}
                      {scopeCapped ? <span className={NOTE_CLASS}>{scopeLabel} has more sessions than one check covers, so this covers the first 500.</span> : null}
                      <span className="flex items-center gap-[6px]">
                        <button type="button" data-lite-std-plan-go="" disabled={!plan.notYetAsked || overLimit} onClick={std.ask} className={PRIMARY}>Check</button>
                        <button type="button" onClick={std.cancelPlan} className={ACTION_CHIP}>Cancel</button>
                        <span className={TEXT}>Only findings not checked yet are sent to the model.</span>
                      </span>
                    </>
                  )}
                </div>
              ) : null}

              <textarea data-lite-standard="" value={editText} onChange={(e) => setEditText(e.target.value)} rows={10} maxLength={STANDARD_MAX_CHARS} spellCheck={false} aria-label="The standard a finding must meet to be escalated to a person" className={`resize-y ${FIELD}`} />
              {draftDiffers ? (
                <div data-lite-std-draft="" className="flex flex-wrap items-center gap-x-[8px] gap-y-[6px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[8px] py-[6px]">
                  <TryControl trial={trial} canTry={canTry && Boolean(editText.trim())} tryBlocked={tryBlocked} fromWindow={fromWindow} onTry={(count) => trial.run(editText, { standardId: inUse.standardId, count })} />
                  <span className="ml-auto flex items-center gap-[6px]">
                    <button type="button" data-lite-std-undo="" onClick={undo} className={ACTION_CHIP}>Discard changes</button>
                    <button type="button" data-lite-std-revise="" disabled={!canWrite || !editText.trim()} onClick={saveEdit} className={PRIMARY}>Save as v{(Number(inUse.version) || 1) + 1}</button>
                  </span>
                </div>
              ) : null}
              {draftDiffers ? (
                <span data-lite-std-try-note="" className={NOTE_CLASS}>
                  {canTry ? "Try runs in your browser; nothing is saved." : tryBlocked}
                  {canWrite ? null : " You need permission to change tenant settings to save it."}
                </span>
              ) : null}
              <StandardTryResults trial={trial} text={editText} windowName={windowName} against={`v${inUse.version}`} />
              <span className={NOTE_CLASS}>
                Saving makes this {inUse.name} v{(Number(inUse.version) || 1) + 1}. What v{inUse.version} flagged is kept. A finding is only flagged if the model says yes twice.
              </span>
            </div>
          )}
        </div>
      ) : null}
      </div>

      {std.error ? <div role="alert" data-lite-std-error="" className={`${BOX} ${TEXT}`}>{std.error}</div> : null}
      {std.answersError ? (
        <div role="alert" data-lite-std-answers-error="" className={`${BOX} ${TEXT}`}>
          Couldn&rsquo;t load the standards&rsquo; results, so nothing shows as flagged: {String(std.answersError?.serverMessage || std.answersError?.message || "the request failed").slice(0, 200)}
        </div>
      ) : null}

      {job ? (
        <div data-lite-std-job="" className={`${BOX} flex flex-wrap items-center gap-x-[10px] gap-y-[6px]`}>
          {job.running ? (
            <>
              <span className={FILE_LABEL}>Checking</span>
              <span className="text-[11px] leading-[1.35] text-[color:var(--ink)]">{job.label}</span>
              <div className="flex h-[4px] min-w-[120px] flex-1 overflow-hidden rounded-[2px] bg-[color:var(--line-soft)]" role="img" aria-label={`${job.done} of ${job.total} asked`}>
                <div className="h-full bg-[color:var(--ink)] transition-[width] duration-300" style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }} />
              </div>
              <span className={TEXT}><strong className="font-medium text-[color:var(--ink)]">{job.done}</strong> of {job.total}{" · "}{usd(job.costUsd || 0)} spent</span>
            </>
          ) : (
            <>
              <span data-lite-std-result="" className="text-[11px] leading-[1.35] text-[color:var(--ink)]">
                {job.error ? `Couldn't check ${job.label}: ${job.error}` : `${job.label} checked ${plural(job.done, "finding")} · ${job.met} flagged · ${usd(job.costUsd || 0)}`}
              </span>
              <button type="button" onClick={std.dismiss} className={`${ACTION_CHIP} ml-auto`}>Dismiss</button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

// A standard's answer on one finding, in the chips the rest of the list uses:
// met filled like a look now, not met outlined like routine, a yes that did
// not repeat dashed in ink, and a finding never put to it dashed and quiet.
const AnswerChip = ({ a }) => {
  if (!a) return <span className={`${CAPS_CHIP} w-full border-dashed border-[color:var(--line)] text-[color:var(--muted-soft)]`}>not checked</span>;
  if (a.escalates) return <span className={`${CAPS_CHIP} w-full ${RUNG_CHIP.look_now}`}>flagged</span>;
  return <span className={`${CAPS_CHIP} w-full ${a.unconfirmed ? "border-dashed border-[color:var(--ink)] text-[color:var(--ink)]" : RUNG_CHIP.routine}`}>{a.unconfirmed ? "unsure" : "no"}</span>;
};

const CARD_COLUMNS = 5;
// Wide enough for the longest answer, "not checked", in the list's chip type.
const ANSWER_WIDTH = 84;
// The narrowest a finding's title stands beside its answers, the room between
// columns, and the card's padding.
const TITLE_MIN = 120;
const COLUMN_GAP = 8;
const CARD_PAD = 12;
// As wide as the card grows: a title and CARD_COLUMNS answers in its padding
// and frame. In a narrower list it takes the list's width and fewer answers.
export const STANDARDS_CARD_W = TITLE_MIN + CARD_COLUMNS * (ANSWER_WIDTH + COLUMN_GAP) + 2 * CARD_PAD + 2;
// The standards a row is judged by: the ones on, in-use first. One turned off
// no longer flags anything, as the list no longer counts it.
const allStandards = ({ lib, inUse }) => [inUse, ...(lib?.items || []).filter((s) => s.id !== inUse?.id)].filter((s) => s?.live);
// The standards in view that flagged any of these findings, in-use first.
export function flaggedBy(members, std) {
  if (!std) return [];
  const standards = allStandards(std);
  return standards.filter((s) => members.some((m) => answerFor(std.answers, s.digest, m.id, std.verdicts?.[m.id])?.escalates));
}
const metUnder = (std, standards, m) => standards.filter((s) => answerFor(std.answers, s.digest, m.id, std.verdicts?.[m.id])?.escalates);
// The findings of a group Triage put to a standard, as every kept standard
// answered them: the ones any standard met first, since they are why the row
// is where it is, then the rest.
// A finding Triage found false, could not check, or has not reached was never
// asked, so it is counted under the board rather than given a row of "not
// asked". Empty, the group has no board to show.
export function boardInGroup(members, std) {
  if (!std) return [];
  const standards = allStandards(std);
  const rank = (m) => (metUnder(std, standards, m).length ? 0 : 1);
  return members
    .filter((m) => ASKABLE_VERDICTS.includes(std.verdicts?.[m.id]?.verdict))
    .map((m, i) => ({ m, i, r: rank(m) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.m);
}
// The card's columns: every standard that met a finding in the group, then
// others up to the card's width (`room` columns), those that answered one
// before those never asked here. Every kept standard counts alike, so none is
// singled out. A standard that met is never cut for width; a scope keeping
// more than five used to drop the one that put the group in the list, and
// the row showed no card at all.
function cardColumns(std, members, room = CARD_COLUMNS) {
  const standards = allStandards(std);
  const met = new Set(members.flatMap((m) => metUnder(std, standards, m).map((s) => s.id)));
  const answered = (s) => members.some((m) => answerFor(std.answers, s.digest, m.id, std.verdicts?.[m.id]));
  const others = standards.filter((s) => !met.has(s.id));
  const ordered = [
    ...standards.filter((s) => met.has(s.id)),
    ...others.filter(answered),
    ...others.filter((s) => !answered(s)),
  ];
  return ordered.slice(0, Math.max(room, met.size));
}

// How every kept standard answered a group, finding by finding: which one
// escalated it, or that none did. Names and titles wrap to two lines rather
// than cutting to a few letters: the card is read, not clicked, so nothing on
// it can be hovered for the rest. `bare` drops the card's own padding where
// the tooltip that holds it already has some.
const NEVER_ASKED = { refuted: "false", unverifiable: "couldn't check", non_adverse: "not about an action" };
export function LiteStandardsCard({ members, std, bare = false }) {
  const { answers, verdicts } = std;
  // How many answers fit beside a title in the width the card has: the
  // welcome page's list is narrower than the Reviews lens's, and a card that
  // kept five there ran past the list's edge, its last answers cut off.
  const ref = useRef(null);
  const [room, setRoom] = useState(CARD_COLUMNS);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const cs = getComputedStyle(el);
      const w = el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      if (w > 0) setRoom(Math.max(1, Math.min(CARD_COLUMNS, Math.floor((w - TITLE_MIN) / (ANSWER_WIDTH + COLUMN_GAP)))));
    };
    measure();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const board = boardInGroup(members, std);
  if (!board.length) return null;
  const cols = cardColumns(std, members, room);
  const metBy = (m) => metUnder(std, cols, m);
  // When any finding met a standard, the card lists only those: they are why
  // the group needs a person.
  const metOnes = board.filter((m) => metBy(m).length);
  const anyMet = metOnes.length > 0;
  const shown = anyMet ? metOnes : board;
  // Why each finding outside the board was never put to a standard.
  const outside = {};
  for (const m of members) {
    if (shown.includes(m)) continue;
    const why = NEVER_ASKED[verdicts?.[m.id]?.verdict] || "not checked yet";
    outside[why] = (outside[why] || 0) + 1;
  }
  const rest = Object.values(outside).reduce((n, k) => n + k, 0);
  // Answers narrow only where the standards that met need more room than the
  // card has.
  const grid = { gridTemplateColumns: `minmax(${TITLE_MIN}px,1fr) repeat(${cols.length}, minmax(56px, ${ANSWER_WIDTH}px))`, columnGap: COLUMN_GAP };
  return (
    <div ref={ref} data-lite-std-card="" className="grid gap-[6px] text-left" style={bare ? undefined : { padding: `9px ${CARD_PAD}px 10px` }}>
      <div className="grid items-end" style={grid}>
        <span className={FILE_LABEL}>{anyMet ? "Flagged" : "Not flagged"}</span>
        {cols.map((s) => (
          <span key={s.id} className="grid min-w-0 content-end gap-[1px]">
            <OverflowTooltip
              lines={2}
              text={standardLabel(s)}
              className="break-words text-[9px] leading-[1.25] text-[color:var(--muted)]"
            >
              {s.name} <span className="font-mono font-normal text-[color:var(--muted-soft)]">v{s.version}</span>
            </OverflowTooltip>
          </span>
        ))}
      </div>
      {shown.map((m) => {
        const v = verdicts[m.id];
        // The reason of the standard that met it, or else of the first that answered.
        const firstAnswered = cols.find((s) => answerFor(answers, s.digest, m.id, v));
        const mine = answerFor(answers, metBy(m)[0]?.digest || firstAnswered?.digest, m.id, v);
        return (
          <div key={m.id} className="grid gap-[3px] border-t border-[color:var(--line-soft)] pt-[6px]">
            <div className="grid items-start" style={grid}>
              <OverflowTooltip lines={2} text={m.title} className="text-[11px] leading-[1.3] text-[color:var(--ink)]" />
              {cols.map((s) => <AnswerChip key={s.id} a={answerFor(answers, s.digest, m.id, v)} />)}
            </div>
            {mine?.standardWhy ? <span className={TEXT}>{mine.standardWhy}</span> : null}
          </div>
        );
      })}
      {rest && !anyMet ? (
        <span data-lite-std-outside="" className={`${TEXT} border-t border-[color:var(--line-soft)] pt-[6px]`}>
          {plural(rest, "other finding")} never put to a standard: {Object.entries(outside).map(([why, n]) => `${n} ${why}`).join(", ")}.
        </span>
      ) : null}
      {/* The chip is too narrow to say it: a yes the second ask did not repeat. */}
      {shown.some((m) => cols.some((c) => answerFor(answers, c.digest, m.id, verdicts[m.id])?.unconfirmed)) ? (
        <span data-lite-std-unsure-note="" className={TEXT}>Unsure: the model said yes once and no the second time, so it isn't flagged.</span>
      ) : null}
    </div>
  );
}
